import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, LayersFront, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AtsGetJobPostingSettingsEvaluationFormsDocument,
  AtsJobPostingSettingsEvaluationFormFragment,
  AtsSaveAsTemplateJobPostingEvaluationFormDocument,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteEvaluationForm } from 'modules/ats/components/EvaluationForm/components/DeleteEvaluationForm'

import { AddEvaluationForm } from './AddEvaluationForm'
import { EvaluationFormSidepanel } from './EvaluationFormSidepanel'

interface Props {
  jobId: number
}

export const EvaluationForms = ({ jobId }: Props) => {
  const [addEvaluationFormOpen, setAddEvaluationFormOpen] = useState(false)
  const [selectedEvaluationForm, setSelectedEvaluationForm] = useState<
    AtsJobPostingSettingsEvaluationFormFragment | undefined
  >(undefined)
  const [deleteItem, setDeleteItem] = useState<
    AtsJobPostingSettingsEvaluationFormFragment | undefined
  >(undefined)

  const [saveAsTemplate] = useMutation(AtsSaveAsTemplateJobPostingEvaluationFormDocument, {
    extractorFn: (data) => data.atsMutations.saveAsTemplateAtsEvaluationForm,
  })

  const onSaveAsTemplate = async (item: AtsJobPostingSettingsEvaluationFormFragment) => {
    const result = await saveAsTemplate({
      variables: { id: item.id },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessEvaluationForm)
  }, [])

  const source = useGraphqlDataCollectionSource({
    document: AtsGetJobPostingSettingsEvaluationFormsDocument,
    mapData: (data) => data.ats.evaluationFormsConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.evaluationFormsConnection.nodes.length,
    }),
    mapVariables: () => ({
      id: jobId,
    }),
    primaryActions: () => ({
      label: i18n.t('common.add'),
      icon: Add,
      onClick: () => setAddEvaluationFormOpen(true),
    }),
    itemOnClick: (item) => () => setSelectedEvaluationForm(item),
    itemActions: (item) => [
      {
        label: i18n.t('common.edit'),
        icon: Pencil,
        onClick: () => setSelectedEvaluationForm(item),
      },
      {
        label: i18n.t('ats.job_posting_settings.evaluation_forms.actions.save_as_template'),
        icon: LayersFront,
        onClick: () => onSaveAsTemplate(item),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        onClick: () => setDeleteItem(item),
        critical: true,
      },
    ],
  })

  return (
    <Box padding="s32">
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('common.name'),
                  render: (item) => item.name,
                },
                {
                  label: i18n.t('ats.evaluation_forms.settings.questions_label'),
                  render: (item) => item.questions.map((q) => q.text).join('; '),
                },
              ],
            },
          },
        ]}
      />

      {selectedEvaluationForm && (
        <EvaluationFormSidepanel
          item={selectedEvaluationForm}
          onClose={() => setSelectedEvaluationForm(undefined)}
        />
      )}

      {addEvaluationFormOpen && (
        <AddEvaluationForm jobId={jobId} onClose={() => setAddEvaluationFormOpen(false)} />
      )}

      {deleteItem && (
        <DeleteEvaluationForm
          evaluationForm={deleteItem}
          onClose={() => setDeleteItem(undefined)}
        />
      )}
    </Box>
  )
}
